import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { editHigherAdminFeedbackRequest2 } from "services/appealService";
import { HigherAdminFeedbackRequest } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  feedbackId: number;
  higherAdmin: HigherAdminFeedbackRequest;
  onClose: (refetch: boolean) => void;
};

const RegistrarSubmittedToHigherAdmin: FC<Props> = ({
  feedbackId,
  higherAdmin,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({
    registrar_number: null,
    registrar_date: null,
  });

  const handleChange = (e: any) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    if (!formState?.registrar_number)
      return t("registration_number_is_required");
    else if (!formState?.registrar_date) return t("registrar_date_is_required");

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    editHigherAdminFeedbackRequest2({
      ...formState,
      id: feedbackId,
      status: "SUBMITTED_TO_HIGHER_ADMIN",
      admin_unit_id: loginData?.structure?.admin_unit?.parent_unit
        ? loginData?.structure?.admin_unit?.parent_unit || ""
        : (higherAdmin?.admin_unit_id ?? null),
      org_unit_id: "",
      previous_admin_unit_id: higherAdmin?.admin_unit_id,
      previous_org_unit_id: higherAdmin?.org_unit_id,
    })
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        navigate("/higher-admin");
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {t("submit_to_higher_admin")}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("submit_to_higher_admin_description")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="flex flex-col mt-4">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              {t("registrar_number")}
            </label>
            <input
              type="text"
              placeholder="#123456"
              className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              name="registrar_number"
              value={formState?.registrar_number ?? ""}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col mt-4">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              {t("registrar_date")}
            </label>
            <input
              type="date"
              className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              name="registrar_date"
              value={
                formState?.registrar_date
                  ? new Date(formState.registrar_date)
                      .toISOString()
                      .split("T")[0]
                  : ""
              }
              onChange={handleChange}
            />
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  {t("loading")}
                </div>
              ) : (
                t("submit")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrarSubmittedToHigherAdmin;
